import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, CheckCircle, XCircle, Loader2, Plus, Trash2, Building2, Lock, Shield, BarChart3, Zap, AlertTriangle } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import type { TenantInfo } from '../lib/auth-context';
import type { UsageData } from '../lib/api';

export function Settings() {
  const { isConnected, isLoading, error, tenant, connect, disconnect, allTenants, activeTenantId, api } = useAuth();
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // Organization management
  const [showAddOrg, setShowAddOrg] = useState(false);
  const [newOrg, setNewOrg] = useState({
    label: '',
    domain: '',
    apiKey: '',
    controlPlaneUrl: '',
  });

  // Fetch usage data when connected
  useEffect(() => {
    if (isConnected && api) {
      setUsageLoading(true);
      api.getUsage()
        .then(setUsage)
        .catch(err => console.error('[Settings] Failed to load usage:', err))
        .finally(() => setUsageLoading(false));
    } else {
      setUsage(null);
    }
  }, [isConnected, api]);

  const handleConnect = async () => {
    if (!url || !apiKey) return;
    setConnecting(true);
    setConnectError(null);
    try {
      await connect(url, apiKey);
      setApiKey('');
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = () => {
    disconnect();
    setUrl('');
    setApiKey('');
    setConnectError(null);
  };

  const handleAddOrg = () => {
    if (!newOrg.label || !newOrg.domain || !newOrg.apiKey || !newOrg.controlPlaneUrl) return;

    connect(newOrg.controlPlaneUrl, newOrg.apiKey)
      .then(() => {
        setNewOrg({ label: '', domain: '', apiKey: '', controlPlaneUrl: '' });
        setShowAddOrg(false);
      })
      .catch((err) => {
        setConnectError(err instanceof Error ? err.message : 'Connection failed');
      });
  };

  const handleRemoveOrg = (tenantId: string) => {
    const tenantsStr = localStorage.getItem('shadowguard_tenants');
    if (!tenantsStr) return;

    const tenants: TenantInfo[] = JSON.parse(tenantsStr);
    const filtered = tenants.filter(t => t.id !== tenantId);
    localStorage.setItem('shadowguard_tenants', JSON.stringify(filtered));
    window.location.reload();
  };

  // PIN Management
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '', '', '']);
  const [pinStep, setPinStep] = useState<'enter' | 'confirm'>('enter');
  const [pinError, setPinError] = useState('');
  const pinInputRefs = useState<(HTMLInputElement | null)[]>([])[0];
  const confirmPinInputRefs = useState<(HTMLInputElement | null)[]>([])[0];
  const hasPinSetup = !!localStorage.getItem('sg_pin_hash');

  const handlePinChange = (index: number, value: string, isConfirm = false) => {
    if (value && !/^\d$/.test(value)) return;
    const refs = isConfirm ? confirmPinInputRefs : pinInputRefs;
    const currentPin = isConfirm ? confirmPin : pin;
    const setCurrentPin = isConfirm ? setConfirmPin : setPin;

    const newPin = [...currentPin];
    newPin[index] = value;
    setCurrentPin(newPin);
    setPinError('');

    if (value && index < 5) {
      refs[index + 1]?.focus();
    }

    if (value && index === 5 && newPin.every(d => d !== '')) {
      if (isConfirm) {
        savePinSetup(pin.join(''), newPin.join(''));
      } else {
        setPinStep('confirm');
        setTimeout(() => confirmPinInputRefs[0]?.focus(), 100);
      }
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent, isConfirm = false) => {
    const refs = isConfirm ? confirmPinInputRefs : pinInputRefs;
    const currentPin = isConfirm ? confirmPin : pin;
    if (e.key === 'Backspace' && !currentPin[index] && index > 0) {
      refs[index - 1]?.focus();
    }
  };

  const savePinSetup = async (pinCode: string, confirmPinCode: string) => {
    if (pinCode !== confirmPinCode) {
      setPinError('PINs do not match');
      setConfirmPin(['', '', '', '', '', '']);
      confirmPinInputRefs[0]?.focus();
      return;
    }

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(pinCode);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      localStorage.setItem('sg_pin_hash', hashHex);
      localStorage.setItem('sg_pin_created', new Date().toISOString());

      setShowPinSetup(false);
      setPin(['', '', '', '', '', '']);
      setConfirmPin(['', '', '', '', '', '']);
      setPinStep('enter');
      setPinError('');
    } catch (err) {
      setPinError('Failed to set PIN');
    }
  };

  const handleRemovePin = () => {
    if (confirm('Are you sure you want to remove your PIN? You will need to enter your API key to login next time.')) {
      localStorage.removeItem('sg_pin_hash');
      localStorage.removeItem('sg_pin_created');
    }
  };

  const planName = usage?.tenant.tier === 'pro' ? 'Growth'
    : usage?.tenant.tier === 'enterprise' ? 'Enterprise'
    : usage?.tenant.tier === 'lite' ? 'Free'
    : 'Unknown';

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-white mb-8">Settings</h2>

      <div className="space-y-6 max-w-2xl">
        {/* Usage Meter */}
        {isConnected && usage && (
          <div className="bg-sg-card border border-sg-border rounded-xl p-6" id="billing">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Usage
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  {planName} plan &mdash; {usage.usage.daily.current.toLocaleString()} of {usage.usage.daily.limit.toLocaleString()} events today
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                usage.usage.daily.percentUsed >= 90
                  ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                  : usage.usage.daily.percentUsed >= 70
                  ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                  : 'bg-green-500/10 text-green-400 border border-green-500/20'
              }`}>
                {usage.usage.daily.percentUsed}%
              </span>
            </div>

            {/* Usage Bar */}
            <div className="w-full bg-sg-dark rounded-full h-3 mb-3">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${
                  usage.usage.daily.percentUsed >= 90
                    ? 'bg-red-500'
                    : usage.usage.daily.percentUsed >= 70
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(usage.usage.daily.percentUsed, 100)}%` }}
              />
            </div>

            {/* Monthly stats row */}
            <div className="flex gap-4 text-sm text-gray-400">
              <span>Monthly total: {usage.usage.monthly.total.toLocaleString()}</span>
              <span>Peak day: {usage.usage.monthly.peakDay.toLocaleString()}</span>
            </div>

            {/* Upgrade prompt for lite tier */}
            {usage.currentPlan.canUpgrade && (
              <div className="mt-4 p-4 bg-sg-accent/5 border border-sg-accent/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-sg-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white">Unlock deceptions and more events</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Upgrade to Growth plan ($49/mo) for 5M events/day, deception infrastructure, and STIX threat intelligence export.
                    </p>
                    <a
                      href={usage.plans.find(p => p.id === 'pro')?.upgradeUrl || '#'}
                      className="inline-block mt-2 px-4 py-1.5 bg-sg-accent text-white rounded-lg text-sm font-medium hover:bg-sg-accent/90 transition-colors"
                    >
                      Upgrade to Growth
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Near-limit warning */}
            {usage.usage.daily.percentUsed >= 90 && usage.usage.daily.percentUsed < 100 && (
              <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-300">
                  You are approaching your daily event limit. {usage.usage.daily.remaining.toLocaleString()} events remaining.
                  {usage.currentPlan.canUpgrade && ' Upgrade to increase your limit.'}
                </p>
              </div>
            )}

            {/* Limit reached */}
            {usage.usage.daily.percentUsed >= 100 && (
              <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-300">
                  Daily event limit reached. New telemetry will be rejected until tomorrow.
                  {usage.currentPlan.canUpgrade && ' Upgrade to Growth to increase your limit.'}
                </p>
              </div>
            )}

            {/* Feature comparison table */}
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-300 mb-3">Plan Features</h4>
              <div className="space-y-2">
                {usage.plans.map(plan => (
                  <div
                    key={plan.id}
                    className={`p-3 rounded-lg border ${
                      plan.id === usage.tenant.tier
                        ? 'bg-sg-accent/5 border-sg-accent/20'
                        : 'bg-sg-dark border-sg-border'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-white">{plan.name}</span>
                        <span className="text-xs text-gray-400 ml-2">
                          {plan.price === null ? 'Custom' : `$${plan.price}/mo`}
                          &nbsp;&mdash; {plan.eventsPerDay === null ? 'Unlimited' : `${(plan.eventsPerDay / 1000000).toFixed(1)}M`} events/day
                        </span>
                      </div>
                      {plan.id === usage.tenant.tier && (
                        <span className="text-xs text-sg-accent font-medium">Current</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.entries(plan.features).map(([feature, enabled]) => (
                        <span
                          key={feature}
                          className={`text-xs px-2 py-0.5 rounded ${
                            enabled
                              ? 'bg-green-500/10 text-green-400'
                              : 'bg-gray-500/10 text-gray-500'
                          }`}
                        >
                          {feature === 'threatDetection' ? 'Threat Detection' :
                           feature === 'dashboards' ? 'Dashboards' :
                           feature === 'deceptions' ? 'Deceptions' :
                           feature === 'stixExport' ? 'STIX Export' :
                           feature === 'multiAgent' ? 'Multi-Agent' :
                           feature === 'siemIntegrations' ? 'SIEM Integration' :
                           feature}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Connection Status */}
        <div className={`bg-sg-card border rounded-xl p-6 ${isConnected ? 'border-sg-green/30' : 'border-sg-border'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Connection Status</h3>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <CheckCircle className="w-5 h-5 text-sg-green" />
                  <span className="text-sg-green text-sm font-medium">Connected</span>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-500 text-sm">Disconnected</span>
                </>
              )}
            </div>
          </div>

          {isConnected && tenant && (
            <div className="space-y-2 mb-4 p-4 bg-sg-dark rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Tenant</span>
                <span className="text-white">{tenant.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Tier</span>
                <span className="text-white capitalize">{planName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Tenant ID</span>
                <span className="text-gray-300 font-mono text-xs">{tenant.id}</span>
              </div>
            </div>
          )}

          {isConnected ? (
            <button
              onClick={handleDisconnect}
              className="px-4 py-2 bg-sg-red/10 text-sg-red border border-sg-red/20 rounded-lg text-sm font-medium hover:bg-sg-red/20 transition-colors"
            >
              Disconnect
            </button>
          ) : null}
        </div>

        {/* API Configuration (show when disconnected) */}
        {!isConnected && (
          <div className="bg-sg-card border border-sg-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">API Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Control Plane URL</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://shadowguard-control-plane-v2.simeon-garratt.workers.dev"
                  className="w-full bg-sg-dark border border-sg-border rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-sg-accent"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">API Key</label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sg_..."
                  className="w-full bg-sg-dark border border-sg-border rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-sg-accent"
                />
              </div>

              {(connectError || error) && (
                <div className="p-3 bg-sg-red/10 border border-sg-red/20 rounded-lg text-sg-red text-sm">
                  {connectError || error}
                </div>
              )}

              <button
                onClick={handleConnect}
                disabled={connecting || !url || !apiKey}
                className="flex items-center gap-2 px-4 py-2 bg-sg-accent text-white rounded-lg text-sm font-medium hover:bg-sg-accent/90 transition-colors disabled:opacity-50"
              >
                {connecting && <Loader2 className="w-4 h-4 animate-spin" />}
                {connecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        )}

        {/* PIN Security */}
        {isConnected && (
          <div className="bg-sg-card border border-sg-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  PIN Security
                </h3>
                <p className="text-sm text-gray-400 mt-1">Set up a 6-digit PIN for quick login</p>
              </div>
              {!hasPinSetup ? (
                <button
                  onClick={() => setShowPinSetup(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg text-sm font-medium hover:bg-green-500/20 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Set Up PIN
                </button>
              ) : (
                <button
                  onClick={handleRemovePin}
                  className="flex items-center gap-2 px-3 py-2 bg-sg-red/10 text-sg-red border border-sg-red/20 rounded-lg text-sm font-medium hover:bg-sg-red/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove PIN
                </button>
              )}
            </div>

            {hasPinSetup && (
              <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-sm font-medium text-white">PIN Enabled</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      You can now login with just your 6-digit PIN
                    </p>
                  </div>
                </div>
              </div>
            )}

            {showPinSetup && (
              <div className="mt-4 p-6 bg-sg-dark border border-sg-border rounded-lg">
                <div className="text-center mb-6">
                  <h4 className="text-lg font-semibold text-white mb-2">
                    {pinStep === 'enter' ? 'Create Your PIN' : 'Confirm Your PIN'}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {pinStep === 'enter'
                      ? 'Enter a 6-digit PIN to secure your dashboard'
                      : 'Re-enter your PIN to confirm'
                    }
                  </p>
                </div>

                <div className="flex justify-center gap-2 mb-4">
                  {(pinStep === 'enter' ? pin : confirmPin).map((digit, index) => (
                    <input
                      key={index}
                      ref={el => pinStep === 'enter' ? pinInputRefs[index] = el : confirmPinInputRefs[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handlePinChange(index, e.target.value, pinStep === 'confirm')}
                      onKeyDown={(e) => handlePinKeyDown(index, e, pinStep === 'confirm')}
                      className={`w-12 h-14 text-center text-2xl font-bold bg-gray-900 border ${
                        pinError ? 'border-red-500' : 'border-white/[0.1] focus:border-green-500'
                      } rounded-lg text-white focus:outline-none transition-colors`}
                    />
                  ))}
                </div>

                {pinError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
                    <p className="text-red-400 text-sm text-center">{pinError}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowPinSetup(false);
                      setPin(['', '', '', '', '', '']);
                      setConfirmPin(['', '', '', '', '', '']);
                      setPinStep('enter');
                      setPinError('');
                    }}
                    className="flex-1 px-4 py-2 bg-sg-card border border-sg-border text-gray-400 rounded-lg text-sm font-medium hover:bg-sg-dark transition-colors"
                  >
                    Cancel
                  </button>
                </div>

                <div className="mt-4 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                  <div className="flex gap-2">
                    <Shield className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-300">
                      Your PIN is encrypted and stored locally. API keys are not persisted in local storage.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Organizations Management */}
        <div className="bg-sg-card border border-sg-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organizations
              </h3>
              <p className="text-sm text-gray-400 mt-1">Manage multiple organizations and switch between them</p>
            </div>
            <button
              onClick={() => setShowAddOrg(!showAddOrg)}
              className="flex items-center gap-2 px-3 py-2 bg-sg-accent/10 text-sg-accent border border-sg-accent/20 rounded-lg text-sm font-medium hover:bg-sg-accent/20 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Organization
            </button>
          </div>

          {/* Add Organization Form */}
          {showAddOrg && (
            <div className="mb-6 p-4 bg-sg-dark border border-sg-border rounded-lg space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Organization Name</label>
                  <input
                    type="text"
                    value={newOrg.label}
                    onChange={(e) => setNewOrg({ ...newOrg, label: e.target.value })}
                    placeholder="Simeon Garratt"
                    className="w-full bg-sg-card border border-sg-border rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-sg-accent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Domain</label>
                  <input
                    type="text"
                    value={newOrg.domain}
                    onChange={(e) => setNewOrg({ ...newOrg, domain: e.target.value })}
                    placeholder="simeongarratt.com"
                    className="w-full bg-sg-card border border-sg-border rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-sg-accent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Control Plane URL</label>
                <input
                  type="text"
                  value={newOrg.controlPlaneUrl}
                  onChange={(e) => setNewOrg({ ...newOrg, controlPlaneUrl: e.target.value })}
                  placeholder="https://shadowguard-control-plane-v2.your-worker.workers.dev"
                  className="w-full bg-sg-card border border-sg-border rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-sg-accent"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">API Key</label>
                <input
                  type="password"
                  value={newOrg.apiKey}
                  onChange={(e) => setNewOrg({ ...newOrg, apiKey: e.target.value })}
                  placeholder="sg_..."
                  className="w-full bg-sg-dark border border-sg-border rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-sg-accent"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddOrg}
                  disabled={!newOrg.label || !newOrg.domain || !newOrg.apiKey || !newOrg.controlPlaneUrl}
                  className="px-4 py-2 bg-sg-accent text-white rounded-lg text-sm font-medium hover:bg-sg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Organization
                </button>
                <button
                  onClick={() => {
                    setShowAddOrg(false);
                    setNewOrg({ label: '', domain: '', apiKey: '', controlPlaneUrl: '' });
                  }}
                  className="px-4 py-2 bg-sg-card border border-sg-border text-gray-400 rounded-lg text-sm font-medium hover:bg-sg-dark transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Organizations List */}
          <div className="space-y-2">
            {allTenants.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                No organizations configured yet
              </div>
            ) : (
              allTenants.map((org) => (
                <div
                  key={org.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                    org.id === activeTenantId
                      ? 'bg-sg-accent/10 border-sg-accent/30'
                      : 'bg-sg-dark border-sg-border hover:border-sg-border/50'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-white">{org.label}</h4>
                      {org.id === activeTenantId && (
                        <span className="px-2 py-0.5 bg-sg-accent/20 text-sg-accent text-xs rounded font-medium">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{org.domain}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveOrg(org.id)}
                    className="p-2 text-gray-400 hover:text-sg-red hover:bg-sg-red/10 rounded-lg transition-colors"
                    title="Remove organization"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
